import React from "react";
import { useTranslation } from "react-i18next";

/**
 * FloorSelector — รายการเลือกชั้นอาคาร (radio-style)
 * ใช้ร่วมกันระหว่าง AddBuilding3 และ AddBuildingEdit2
 *
 * Props:
 *  - items         : Array<{ id, title, floor, desc }> — รายการชั้น
 *  - activeId      : any    — id ของชั้นที่ถูกเลือก
 *  - onSelect      : func   — callback(id) เมื่อเลือกชั้น
 *  - onAddFloor    : func   — callback เมื่อกดปุ่ม "เพิ่มจำนวนชั้นภายใน"
 */
export default function FloorSelector({ items, activeId, onSelect, onAddFloor }) {
  const { t } = useTranslation();

  return (
    <div className="font-semibold text-slate-900">
      {/* หัวข้อส่วน */}
      {t("admin.addBuilding.floorsCountValHeader")}
      <div className="border-b-2 border-slate-200 pb-2 mb-4" />

      <div className="space-y-3">
        {/* รายการชั้นแต่ละชั้น */}
        {items.map((item) => {
          const isActive = activeId === item.id;

          return (
            <button
              key={item.id}
              onClick={() => onSelect(item.id)}
              aria-pressed={isActive}
              className={`w-fit flex gap-3 p-3 text-left rounded-lg border outline-none transition ${
                isActive
                  ? "border-blue-500 border-2 bg-blue-50"
                  : "border-slate-200 bg-white hover:border-blue-300"
              }`}
            >
              {/* วงกลม radio-style */}
              <span
                className={`w-5 h-5 mt-3 ml-2 rounded-full border flex items-center justify-center ${
                  isActive
                    ? "border-blue-500 border-[6px]"
                    : "border-slate-400 bg-slate-300"
                }`}
              >
                {isActive && <span className="rounded-full" />}
              </span>

              {/* ข้อมูลชั้น */}
              <div className="flex-1 min-w-0">
                {/* ชื่ออาคาร + หมายเลขชั้น */}
                <div className="flex items-start justify-between gap-3">
                  <div className="font-base text-slate-900 mt-2">{item.title}</div>
                  <div className="shrink-0 font-medium text-slate-900 p-2">
                    {item.floor}
                  </div>
                </div>

                {/* คำอธิบาย */}
                <div
                  className={`text-xs mt-2 ${
                    isActive ? "text-blue-600" : "text-slate-500"
                  }`}
                >
                  {item.desc}
                </div>
              </div>
            </button>
          );
        })}

        {/* ปุ่มเพิ่มชั้น */}
        <button
          onClick={onAddFloor}
          className="w-full p-3 border-2 border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition"
        >
          {t("admin.addBuilding.addInternalFloor")}
        </button>
      </div>
    </div>
  );
}
